import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { canAccessEngineeringModule } from '../../utils/roleConstants';
import { machineSettingsService as service } from '../../_services/machineSettings.service';
import { CategoryForm } from './CategoryForm';
import { CategoryTable } from './CategoryTable';
import { MachineForm } from './MachineForm';

const sortMachines = (rows) =>
    [...rows].sort((left, right) => left.machine.localeCompare(right.machine));

function ProductionConfigurationContent() {
    const [rows, setRows] = useState(null);
    const [categories, setCategories] = useState(null);
    const [category, setCategory] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(null);
    const [categoryForm, setCategoryForm] = useState(null);
    const [accessDenied, setAccessDenied] = useState(false);
    const sequence = useRef(0);
    const busy = useRef(false);
    const denied = useRef(false);
    const mounted = useRef(true);
    const refreshRef = useRef(null);

    useEffect(() => {
        mounted.current = true;

        let disposed = false;
        let pending = false;

        const refresh = (force = true) => {
            if (disposed || pending || busy.current || denied.current) return;

            pending = true;
            const version = ++sequence.current;
            setRefreshing(true);
            const machines = service.list(force);
            const machineCategories = service.categories(force);

            if (machines.data !== null) setRows(machines.data);
            if (machineCategories.data !== null) setCategories(machineCategories.data);

            Promise.all([machines.revalidate, machineCategories.revalidate])
                .then(([machineData, categoryData]) => {
                    if (!disposed && version === sequence.current) {
                        setRows(machineData);
                        setCategories(categoryData);
                        setError('');
                    }
                })
                .catch((err) => {
                    if (!disposed && version === sequence.current) {
                        setError(err.message);

                        if (err.status === 401 || err.status === 403) {
                            denied.current = true;
                            setAccessDenied(true);
                            setRows(null);
                            setCategories(null);
                        }
                    }
                })
                .finally(() => {
                    pending = false;

                    if (!disposed) setRefreshing(false);
                });
        };
        refreshRef.current = refresh;
        refresh(false);

        const refreshVisible = () => {
            if (document.visibilityState === 'visible' && navigator.onLine !== false) refresh();
        };

        const timer = window.setInterval(refreshVisible, 30000);
        window.addEventListener('focus', refreshVisible);
        window.addEventListener('online', refreshVisible);
        document.addEventListener('visibilitychange', refreshVisible);

        return () => {
            disposed = true;
            mounted.current = false;
            sequence.current += 1;
            window.clearInterval(timer);
            window.removeEventListener('focus', refreshVisible);
            window.removeEventListener('online', refreshVisible);
            document.removeEventListener('visibilitychange', refreshVisible);
        };
    }, []);

    // Shows the change at once, sends it, then keeps the server's copy or
    // restores the previous lists if the server refuses it.
    async function mutate(applyOptimistic, send, applySaved) {
        if (busy.current) return;

        busy.current = true;
        sequence.current += 1;
        setSaving(true);
        setError('');

        const previous = { rows, categories };
        applyOptimistic();

        try {
            const saved = await send();

            if (mounted.current) {
                applySaved(saved);
                setForm(null);
                setCategoryForm(null);
            }
        } catch (err) {
            if (mounted.current) {
                setRows(previous.rows);
                setCategories(previous.categories);
                setError(err.message);

                if (err.status === 401 || err.status === 403) {
                    denied.current = true;
                    setAccessDenied(true);
                }
            }
        } finally {
            busy.current = false;

            if (mounted.current) setSaving(false);
        }
    }

    const categoryName = (number) =>
        (categories || []).find((item) => String(item.category) === String(number))?.description;

    function saveMachine(kind, machine, values) {
        const changes =
            kind === 'deactivate'
                ? { active: false }
                : { ...values, description: categoryName(values.category) };
        const optimistic =
            kind === 'create'
                ? { ...changes, id: 'pending', active: true }
                : { ...machine, ...changes };

        return mutate(
            () =>
                setRows(
                    sortMachines(
                        kind === 'create'
                            ? [...rows, optimistic]
                            : rows.map((row) => (row.id === machine.id ? optimistic : row))
                    )
                ),
            () =>
                kind === 'create'
                    ? service.create(values)
                    : kind === 'deactivate'
                      ? service.deactivate(machine.id)
                      : service.update(machine.id, values),
            (saved) => {
                setRows((current) =>
                    sortMachines(current.map((row) => (row.id === optimistic.id ? saved : row)))
                );
                setCategories((current) => current.map((item) => {
                    if (typeof item.assetCount !== 'number') return item;

                    const wasAssigned = machine && String(machine.category) === String(item.category);
                    const isAssigned = String(saved.category) === String(item.category);

                    return {
                        ...item,
                        assetCount: item.assetCount + Number(isAssigned) - Number(!!wasAssigned),
                    };
                }));
            }
        );
    }

    function saveCategory(existing, values) {
        const optimistic = existing
            ? { ...existing, ...values }
            : { ...values, id: 'pending', category: null, assetCount: 0 };

        return mutate(
            () => {
                setCategories(
                    existing
                        ? categories.map((item) => (item.id === existing.id ? optimistic : item))
                        : [...categories, optimistic]
                );

                // A machine's description is its category's name, so it follows a rename.
                if (existing)
                    setRows(
                        rows.map((row) =>
                            String(row.category) === String(existing.category)
                                ? { ...row, description: values.description }
                                : row
                        )
                    );
            },
            () =>
                existing
                    ? service.updateCategory(existing.id, values)
                    : service.createCategory(values),
            (saved) =>
                setCategories((current) =>
                    current.map((item) => (item.id === optimistic.id ? { ...optimistic, ...saved } : item))
                )
        );
    }

    function deleteCategory(item) {
        if (item.assetCount !== 0) return;

        return mutate(
            () => {},
            () => service.deleteCategory(item.id),
            () => {
                setCategories(current => current.filter(category => category.id !== item.id));

                if (category === String(item.category)) setCategory('');
            }
        );
    }

    if (accessDenied) return <Alert variant="danger">Access denied. {error}</Alert>;

    const loaded = rows !== null && categories !== null;
    const savedCategories = (categories || []).filter((item) => item.id !== 'pending');
    const visibleRows = (rows || []).filter(
        (row) => category === '' || String(row.category) === category
    );
    const openForm = (setter) => (value) => {
        setError('');
        setter(value);
    };
    const showPageError = error && !form && !categoryForm;

    return (
        <>
            {showPageError && (
                <Alert variant="danger" role="alert">
                    {error}
                </Alert>
            )}
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
                <span role="status" className="text-muted">
                    {!loaded
                        ? 'Loading…'
                        : category === ''
                          ? `${rows.length} machines`
                          : `${visibleRows.length} of ${rows.length} machines`}
                    {refreshing && loaded ? ' · Refreshing…' : ''}
                    {saving ? ' · Saving…' : ''}
                </span>
                <Button
                    variant="outline-secondary"
                    disabled={refreshing || saving}
                    onClick={() => refreshRef.current()}
                >
                    Refresh
                </Button>
            </div>
            {!loaded ? (
                refreshing && <Spinner animation="border" size="sm" aria-label="Loading" />
            ) : (
                <>
                    <Card className="mb-4">
                        <Card.Body>
                            <CategoryTable
                                categories={categories}
                                machines={rows}
                                saving={saving}
                                onAdd={() => openForm(setCategoryForm)({ category: null })}
                                onEdit={(item) => openForm(setCategoryForm)({ category: item })}
                                onDelete={deleteCategory}
                            />
                        </Card.Body>
                    </Card>
                    <Card>
                        <Card.Body>
                            <div className="d-flex flex-wrap align-items-end justify-content-between mb-3 gap-2">
                                <div>
                                    <h5>Machines / Lines</h5>
                                    <Form.Group
                                        controlId="machines-category-filter"
                                        style={{ minWidth: 280 }}
                                    >
                                        <Form.Label>Category</Form.Label>
                                        <Form.Select
                                            value={category}
                                            disabled={saving}
                                            onChange={(event) => setCategory(event.target.value)}
                                        >
                                            <option value="">All categories</option>
                                            {savedCategories.map((item) => (
                                                <option key={item.id} value={String(item.category)}>
                                                    {item.description}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="text-end">
                                    <Button
                                        disabled={saving || savedCategories.length === 0}
                                        onClick={() => openForm(setForm)({ machine: null })}
                                    >
                                        Add Machine / Line
                                    </Button>
                                    {savedCategories.length === 0 && (
                                        <div className="form-text">Create a category first.</div>
                                    )}
                                </div>
                            </div>
                            <Table responsive striped hover className="align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Machine</th>
                                        <th>Category ID</th>
                                        <th>Description</th>
                                        <th>Active</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted py-4">
                                                No machines yet. Add the first machine to get
                                                started.
                                            </td>
                                        </tr>
                                    )}
                                    {rows.length > 0 && visibleRows.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted py-4">
                                                No machines match this category.
                                            </td>
                                        </tr>
                                    )}
                                    {visibleRows.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.machine}</td>
                                            <td>{row.category}</td>
                                            <td>{row.description || '—'}</td>
                                            <td>
                                                <Form.Check
                                                    type="switch"
                                                    role="switch"
                                                    className="mb-0 asset-active-toggle"
                                                    checked={!!row.active}
                                                    disabled={saving || row.id === 'pending'}
                                                    aria-label={`Active ${row.machine}`}
                                                    onChange={(event) =>
                                                        saveMachine(
                                                            event.target.checked ? 'update' : 'deactivate',
                                                            row,
                                                            {
                                                                machine: row.machine,
                                                                category: row.category,
                                                                active: event.target.checked,
                                                            }
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        disabled={saving}
                                                        aria-label={`Edit ${row.machine}`}
                                                        onClick={() =>
                                                            openForm(setForm)({ machine: row })
                                                        }
                                                    >
                                                        Edit
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </>
            )}
            {form && (
                <MachineForm
                    machine={form.machine}
                    categories={savedCategories}
                    saving={saving}
                    error={error}
                    onClose={() => setForm(null)}
                    onSave={(values) =>
                        saveMachine(form.machine ? 'update' : 'create', form.machine, values)
                    }
                />
            )}
            {categoryForm && (
                <CategoryForm
                    category={categoryForm.category}
                    saving={saving}
                    error={error}
                    onClose={() => setCategoryForm(null)}
                    onSave={(values) => saveCategory(categoryForm.category, values)}
                />
            )}
        </>
    );
}

export function ProductionConfiguration() {
    let user;

    try {
        user = JSON.parse(localStorage.getItem('user'));
    } catch {
        user = null;
    }

    if (!canAccessEngineeringModule(user))
        return (
            <Alert variant="danger" className="m-4">
                <h4>Access denied.</h4>
            </Alert>
        );

    return (
        <div className="container-fluid py-4">
            <h2>Production Configuration</h2>
            <p className="text-muted">
                Manage the machine categories and machines of your company. Create a category before
                adding its machines. Deactivated machines retain their history.
            </p>
            <ProductionConfigurationContent />
        </div>
    );
}
